import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  getMessageTemplates,
  createMessageTemplate,
  updateMessageTemplate,
  deleteMessageTemplate,
} from '../../utils/campaignApi';
import type { MessageTemplate, MessageContent } from '../../types/CampaignCreation';

type SortField = 'template_name' | 'is_public' | 'messages' | 'service_tier' | 'usage_count' | 'owner_email';
type SortDir = 'asc' | 'desc';

interface TemplateWithOwner extends MessageTemplate {
  owner_email?: string;
}

interface TemplateFormData {
  template_name: string;
  template_description: string;
  service_tier: 'junior_sdr' | 'top_qualifier';
  is_public: boolean;
  messages: MessageContent[];
}

const emptyMessage: MessageContent = {
  message_number: 1,
  content: '',
  character_count: 0,
  delay_days: 0,
};

const emptyForm: TemplateFormData = {
  template_name: '',
  template_description: '',
  service_tier: 'junior_sdr',
  is_public: true,
  messages: [{ ...emptyMessage }],
};

const PAGE_SIZE = 20;

const AdminTemplateManagement: React.FC = () => {
  const [allTemplates, setAllTemplates] = useState<TemplateWithOwner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<TemplateFormData>({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Search, sort, pagination state
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('template_name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage] = useState(0);

  const fetchTemplates = async () => {
    setLoading(true);
    setError(null);
    const result = await getMessageTemplates({ all: true, limit: 500 });
    if (result.success && result.data) {
      setAllTemplates(result.data as TemplateWithOwner[]);
    } else {
      setError(result.error || 'Failed to fetch templates');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  // Reset page when search changes
  useEffect(() => { setPage(0); }, [search]);

  // Filter by search
  const filtered = useMemo(() => {
    if (!search.trim()) return allTemplates;
    const q = search.toLowerCase();
    return allTemplates.filter(t =>
      t.template_name.toLowerCase().includes(q) ||
      (t.template_description || '').toLowerCase().includes(q) ||
      (t.owner_email || '').toLowerCase().includes(q)
    );
  }, [allTemplates, search]);

  // Sort
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let aVal: string | number | boolean;
      let bVal: string | number | boolean;
      switch (sortField) {
        case 'template_name':
          aVal = a.template_name.toLowerCase();
          bVal = b.template_name.toLowerCase();
          break;
        case 'is_public':
          aVal = a.is_public ? 1 : 0;
          bVal = b.is_public ? 1 : 0;
          break;
        case 'messages':
          aVal = a.messages?.length || 0;
          bVal = b.messages?.length || 0;
          break;
        case 'service_tier':
          aVal = a.service_tier;
          bVal = b.service_tier;
          break;
        case 'usage_count':
          aVal = a.usage_count || 0;
          bVal = b.usage_count || 0;
          break;
        case 'owner_email':
          aVal = (a.owner_email || '').toLowerCase();
          bVal = (b.owner_email || '').toLowerCase();
          break;
        default:
          return 0;
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortField, sortDir]);

  // Paginate
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paginated = useMemo(() => {
    const start = page * PAGE_SIZE;
    return sorted.slice(start, start + PAGE_SIZE);
  }, [sorted, page]);

  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(0);
  }, [sortField]);

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return <span className="ml-1 text-gray-300">&#8597;</span>;
    return <span className="ml-1">{sortDir === 'asc' ? '&#9650;' : '&#9660;'}</span>;
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData({ ...emptyForm, messages: [{ ...emptyMessage }] });
    setShowModal(true);
  };

  const openEdit = (template: TemplateWithOwner) => {
    setEditingId(template.id);
    setFormData({
      template_name: template.template_name,
      template_description: template.template_description || '',
      service_tier: template.service_tier,
      is_public: template.is_public,
      messages: template.messages.map((m, i) => ({
        ...m,
        message_number: m.message_number || i + 1,
      })),
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.template_name.trim()) {
      setError('Template name is required');
      return;
    }
    if (formData.messages.length === 0 || !formData.messages[0].content.trim()) {
      setError('At least one message with content is required');
      return;
    }

    setSaving(true);
    setError(null);

    const messages = formData.messages.map((m, i) => ({
      ...m,
      message_number: i + 1,
      character_count: m.content.length,
    }));

    if (editingId) {
      const result = await updateMessageTemplate(editingId, {
        template_name: formData.template_name,
        template_description: formData.template_description,
        service_tier: formData.service_tier,
        is_public: formData.is_public,
        messages,
      } as Partial<MessageTemplate>);
      if (result.success) {
        setShowModal(false);
        fetchTemplates();
      } else {
        setError(result.error || 'Failed to update template');
      }
    } else {
      const result = await createMessageTemplate({
        template_name: formData.template_name,
        template_description: formData.template_description,
        service_tier: formData.service_tier,
        is_public: formData.is_public,
        messages,
        category: 'general',
        tags: [],
        user_id: '',
      });
      if (result.success) {
        setShowModal(false);
        fetchTemplates();
      } else {
        setError(result.error || 'Failed to create template');
      }
    }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    const result = await deleteMessageTemplate(id);
    if (result.success) {
      setDeleteConfirm(null);
      fetchTemplates();
    } else {
      setError(result.error || 'Failed to delete template');
    }
  };

  const updateMessage = (index: number, field: keyof MessageContent, value: string | number) => {
    setFormData(prev => {
      const messages = [...prev.messages];
      messages[index] = { ...messages[index], [field]: value };
      if (field === 'content') {
        messages[index].character_count = (value as string).length;
      }
      return { ...prev, messages };
    });
  };

  const addMessage = () => {
    setFormData(prev => ({
      ...prev,
      messages: [
        ...prev.messages,
        { message_number: prev.messages.length + 1, content: '', character_count: 0, delay_days: 3 },
      ],
    }));
  };

  const removeMessage = (index: number) => {
    if (formData.messages.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      messages: prev.messages.filter((_, i) => i !== index),
    }));
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-3 text-gray-500">Loading templates...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      {/* Header with search + create */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Message Templates</h2>
            <p className="text-sm text-gray-500">{filtered.length} template{filtered.length !== 1 ? 's' : ''} total</p>
          </div>
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            + Create Template
          </button>
        </div>
        {/* Search bar */}
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, description, or owner email..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
          <button onClick={() => setError(null)} className="ml-2 font-medium underline">Dismiss</button>
        </div>
      )}

      {/* Templates Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                onClick={() => handleSort('template_name')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"
              >
                Name <SortIcon field="template_name" />
              </th>
              <th
                onClick={() => handleSort('owner_email')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"
              >
                Owner <SortIcon field="owner_email" />
              </th>
              <th
                onClick={() => handleSort('is_public')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"
              >
                Visibility <SortIcon field="is_public" />
              </th>
              <th
                onClick={() => handleSort('messages')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"
              >
                Messages <SortIcon field="messages" />
              </th>
              <th
                onClick={() => handleSort('service_tier')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"
              >
                Tier <SortIcon field="service_tier" />
              </th>
              <th
                onClick={() => handleSort('usage_count')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"
              >
                Usage <SortIcon field="usage_count" />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  {search ? 'No templates match your search.' : 'No templates found. Create one to get started.'}
                </td>
              </tr>
            ) : (
              paginated.map(template => (
                <tr key={template.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{template.template_name}</div>
                    {template.template_description && (
                      <div className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">
                        {template.template_description}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {template.owner_email
                      ? <span className="truncate block max-w-[180px]" title={template.owner_email}>{template.owner_email}</span>
                      : <span className="text-gray-400">-</span>
                    }
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      template.is_public
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {template.is_public ? 'Public' : 'Private'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {template.messages?.length || 0}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {template.service_tier === 'junior_sdr' ? 'Platform' : 'Top Qualifier'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {template.usage_count || 0}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openEdit(template)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Edit
                      </button>
                      {deleteConfirm === template.id ? (
                        <span className="flex items-center space-x-1">
                          <button
                            onClick={() => handleDelete(template.id)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="text-gray-500 hover:text-gray-700 text-sm"
                          >
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(template.id)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, sorted.length)} of {sorted.length}
          </p>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                className={`px-3 py-1 text-sm rounded-md ${
                  page === i
                    ? 'bg-blue-600 text-white'
                    : 'border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {i + 1}
              </button>
            )).slice(
              Math.max(0, page - 2),
              Math.min(totalPages, page + 3)
            )}
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black bg-opacity-30" onClick={() => setShowModal(false)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingId ? 'Edit Template' : 'Create Template'}
              </h3>

              {/* Name */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
                <input
                  type="text"
                  value={formData.template_name}
                  onChange={e => setFormData(prev => ({ ...prev, template_name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Agency Cold Outreach Sequence"
                />
              </div>

              {/* Description */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={formData.template_description}
                  onChange={e => setFormData(prev => ({ ...prev, template_description: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief description of this template"
                />
              </div>

              {/* Tier + Public toggle */}
              <div className="flex space-x-4 mb-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service Tier</label>
                  <select
                    value={formData.service_tier}
                    onChange={e => setFormData(prev => ({ ...prev, service_tier: e.target.value as 'junior_sdr' | 'top_qualifier' }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="junior_sdr">Platform</option>
                    <option value="top_qualifier">Top Qualifier</option>
                  </select>
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_public}
                      onChange={e => setFormData(prev => ({ ...prev, is_public: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Public (visible to all users)</span>
                  </label>
                </div>
              </div>

              {/* Messages */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Messages</label>
                  <button
                    onClick={addMessage}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    + Add Message
                  </button>
                </div>

                <div className="space-y-4">
                  {formData.messages.map((msg, i) => (
                    <div key={i} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-600">
                          Message {i + 1} {i === 0 ? '(Connection Request)' : '(Follow-up)'}
                        </span>
                        <div className="flex items-center space-x-3">
                          {i > 0 && (
                            <div className="flex items-center space-x-1">
                              <label className="text-xs text-gray-500">Delay (days):</label>
                              <input
                                type="number"
                                min={0}
                                value={msg.delay_days || 0}
                                onChange={e => updateMessage(i, 'delay_days', parseInt(e.target.value) || 0)}
                                className="w-14 border border-gray-300 rounded px-2 py-1 text-xs text-center"
                              />
                            </div>
                          )}
                          {formData.messages.length > 1 && (
                            <button
                              onClick={() => removeMessage(i)}
                              className="text-red-500 hover:text-red-700 text-xs"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                      <textarea
                        value={msg.content}
                        onChange={e => updateMessage(i, 'content', e.target.value)}
                        rows={4}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder={i === 0 ? 'Connection request message (300 char max)' : 'Follow-up message content'}
                      />
                      <div className="text-xs text-gray-400 mt-1 text-right">
                        {msg.content.length} characters
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingId ? 'Update Template' : 'Create Template'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTemplateManagement;
